import type { PortalPromoDto } from '@supply-mover-2-0/shared';

export type PromoTileSize = 'hero' | 'feature' | 'slate' | 'compact' | 'strip';

/** Which `imagesJson` key supplied the artwork for a slot. */
export type PromoArtSourceKey =
	| 'bannerUrl'
	| 'bannerUrl4x3'
	| 'bannerUrlSquare'
	| 'bannerStripUrl'
	| 'thumbnailUrl';

export interface PromoArtResolution {
	url: string | null;
	/** Null when no upload satisfies this slot. */
	source: PromoArtSourceKey | null;
}

/**
 * Pick the artwork a promo tile should render for a given slot.
 *
 * Extracted from `PromoTile` so the admin-side creative preview can
 * report the same answer instead of reimplementing the fallback chain.
 * That duplication is what previously let the preview drift out of sync
 * with the real tile — it advertised aspect ratios and source
 * precedence the storefront had since changed.
 *
 * Admins upload per-aspect variants so each slot gets art composed for
 * its shape; the chains below fall back toward the 3:1 `bannerUrl`,
 * which every promo is expected to have.
 *
 * `strip` is deliberately strict: it uses only the dedicated no-text
 * `bannerStripUrl`. The regular banner has the headline and badge baked
 * in, and a strip slot crops away most of the source's vertical
 * content, so falling back would ship half-clipped text. A missing
 * strip upload renders the gradient placeholder instead.
 */
export function resolvePromoArt(
	imagesJson: PortalPromoDto['imagesJson'],
	size: PromoTileSize,
): PromoArtResolution {
	const imgs = imagesJson;

	const chain: Array<PromoArtSourceKey> =
		size === 'compact'
			? ['bannerUrlSquare', 'thumbnailUrl', 'bannerUrl']
			: size === 'strip'
				? ['bannerStripUrl']
				: size === 'feature' || size === 'slate'
					? ['bannerUrl', 'bannerUrl4x3']
					: ['bannerUrl'];

	for (const key of chain) {
		const candidate = imgs?.[key];
		if (candidate != null && candidate !== '') {
			return { url: candidate, source: key };
		}
	}

	return { url: null, source: null };
}
